import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { ItemFormModal, type ItemKind } from "../components/items/ItemFormModal";
import { StockPriceDrawer } from "../components/items/StockPriceDrawer";
import { HardDeleteConfirm } from "../components/ui/HardDeleteConfirm";
import { Icon } from "../components/ui/Icon";
import { SearchSelect, type SSOption } from "../components/ui/SearchSelect";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";

const KIND_LABELS: Record<ItemKind, { title: string; sub: string; ctaLabel: string; routeSeg: string }> = {
  stock: {
    title: "Stok Kartları",
    sub: "Hammadde, ambalaj, sarf malzemeleri",
    ctaLabel: "Yeni Stok Kartı",
    routeSeg: "stock",
  },
  semi: {
    title: "Yarı Mamuller",
    sub: "Üretilen ve depolanan ara ürünler",
    ctaLabel: "Yeni Yarı Mamul",
    routeSeg: "semi",
  },
  finished: {
    title: "Mamuller",
    sub: "Nihai üretim çıktıları",
    ctaLabel: "Yeni Mamul",
    routeSeg: "finished",
  },
};

const TABS: Array<{ kind: ItemKind; label: string }> = [
  { kind: "stock", label: "Stok" },
  { kind: "semi", label: "Yarı Mamul" },
  { kind: "finished", label: "Mamul" },
];

const CUR_SYMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
function fmtPrice(p: { unitPrice: number; currency: string }): string {
  return `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(p.unitPrice)} ${CUR_SYMBOL[p.currency] ?? p.currency}`;
}

type AnyItemRow = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  baseUomId: string;
  shelfLifeDays: number | null;
  allergens: string[];
  isActive: boolean;
  baseUom?: { code: string; symbol: string } | null;
  // stock-only
  reorderPoint?: unknown;
  leadTimeDays?: number | null;
  currentPrice?: { unitPrice: number; currency: string } | null;
  // semi/finished
  qcRequired?: boolean;
  consumptionMethod?: "backflush" | "manual";
};

export function ItemsPage() {
  const { kind: kindParam } = useParams<{ kind?: string }>();
  const navigate = useNavigate();
  const kind: ItemKind = (kindParam === "semi" || kindParam === "finished") ? kindParam : "stock";
  const isMobile = useIsMobile();

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Filtreler (select list) — kart türü, birim, durum, alerjen
  const [typeId, setTypeId] = useState("");
  const [uomId, setUomId] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "passive">("all");
  const [allergen, setAllergen] = useState("");
  const filtersActive = !!typeId || !!uomId || status !== "all" || !!allergen;
  function clearFilters() {
    setTypeId(""); setUomId(""); setStatus("all"); setAllergen(""); setPage(1);
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AnyItemRow | null>(null);
  const [priceCard, setPriceCard] = useState<AnyItemRow | null>(null);

  // Süper admin: çift onaylı kalıcı silme
  const queryClient = useQueryClient();
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuperAdmin = !!meQ.data?.user?.isSuperAdmin;
  const [delTarget, setDelTarget] = useState<AnyItemRow | null>(null);
  const hardDelStock = trpc.stockCard.hardDelete.useMutation();
  const hardDelSemi = trpc.semiProduct.hardDelete.useMutation();
  const hardDelFinished = trpc.finishedProduct.hardDelete.useMutation();
  const delMut = kind === "stock" ? hardDelStock : kind === "semi" ? hardDelSemi : hardDelFinished;
  function runHardDelete() {
    if (!delTarget) return;
    delMut.mutate({ id: delTarget.id }, {
      onSuccess: () => { setDelTarget(null); delMut.reset(); queryClient.invalidateQueries(); },
    });
  }

  const input = useMemo(() => ({
    page, pageSize,
    q: q.trim() || undefined,
    itemTypeId: typeId || undefined,
    baseUomId: uomId || undefined,
    isActive: status === "all" ? undefined : status === "active",
    allergen: allergen || undefined,
  }), [page, pageSize, q, typeId, uomId, status, allergen]);

  // Filtre seçenek kaynakları (türler kind'e özel) + SearchSelect option'ları
  const uomQ = trpc.uom.list.useQuery(undefined, { staleTime: 5 * 60_000 });
  const typesQ = trpc.settings.itemType.list.useQuery({ kind }, { staleTime: 60_000 });
  const allergensQ = trpc.settings.allergen.list.useQuery(undefined, { staleTime: 60_000 });

  const typeOptions = useMemo<SSOption[]>(
    () => (typesQ.data ?? []).map((t) => ({ value: t.id, label: t.name, tail: t.code })),
    [typesQ.data],
  );
  const uomOptions = useMemo<SSOption[]>(
    () => (uomQ.data ?? []).map((u) => ({ value: u.id, label: u.symbol, tail: u.code !== u.symbol ? u.code : undefined })),
    [uomQ.data],
  );
  const allergenOptions = useMemo<SSOption[]>(
    () => (allergensQ.data ?? []).map((a) => ({ value: a.name, label: a.name })),
    [allergensQ.data],
  );
  const statusOptions: SSOption[] = [
    { value: "active", label: "Aktif" },
    { value: "passive", label: "Pasif" },
  ];

  const stockQ = trpc.stockCard.list.useQuery(input, { enabled: kind === "stock" });
  const semiQ = trpc.semiProduct.list.useQuery(input, { enabled: kind === "semi" });
  const finishedQ = trpc.finishedProduct.list.useQuery(input, { enabled: kind === "finished" });
  const activeQ = kind === "stock" ? stockQ : kind === "semi" ? semiQ : finishedQ;

  const countsStock = trpc.stockCard.list.useQuery({ page: 1, pageSize: 1 });
  const countsSemi = trpc.semiProduct.list.useQuery({ page: 1, pageSize: 1 });
  const countsFinished = trpc.finishedProduct.list.useQuery({ page: 1, pageSize: 1 });
  const tabCounts: Record<ItemKind, number | undefined> = {
    stock: countsStock.data?.total,
    semi: countsSemi.data?.total,
    finished: countsFinished.data?.total,
  };

  const labels = KIND_LABELS[kind];
  const rows = (activeQ.data?.rows ?? []) as unknown as AnyItemRow[];
  const total = activeQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  /**
   * YENİ KART → uygulama içi SEKME (çekmece DEĞİL). Kullanıcı formu doldururken listeye,
   * reçeteye ya da başka bir sayfaya bakıp geri dönebilsin diye. Üç ayrı CTA (masaüstü
   * başlık / mobil başlık / boş-durum) AYNI fonksiyonu çağırır — biri çekmecede kalırsa
   * aynı iş iki farklı yerden iki farklı davranışla açılırdı.
   */
  function openCreate() {
    navigate(`/items/${labels.routeSeg}/yeni`);
  }
  function openEdit(row: AnyItemRow) {
    setEditingItem(row);
    setModalOpen(true);
  }

  const drawers = (
    <>
      <ItemFormModal
        open={modalOpen}
        kind={kind}
        initial={editingItem ? {
          id: editingItem.id,
          code: editingItem.code,
          name: editingItem.name,
          description: editingItem.description,
          baseUomId: editingItem.baseUomId,
          shelfLifeDays: editingItem.shelfLifeDays,
          allergens: editingItem.allergens,
          isActive: editingItem.isActive,
          reorderPoint: editingItem.reorderPoint as number | string | null | undefined,
          leadTimeDays: editingItem.leadTimeDays ?? null,
          qcRequired: editingItem.qcRequired,
          consumptionMethod: editingItem.consumptionMethod,
        } : null}
        onClose={() => setModalOpen(false)}
      />

      <StockPriceDrawer
        open={!!priceCard}
        stockCardId={priceCard?.id ?? null}
        cardCode={priceCard?.code}
        cardName={priceCard?.name}
        baseUomSymbol={priceCard?.baseUom?.symbol ?? null}
        onClose={() => setPriceCard(null)}
      />

      <HardDeleteConfirm
        open={!!delTarget}
        code={delTarget?.code ?? ""}
        title={`${labels.title} — kalıcı sil`}
        warning={
          <>
            <b>{delTarget?.code}</b> — {delTarget?.name} kartını <b>KALICI</b> olarak sileceksiniz.
            Özel alanlar ve (varsa) birim fiyat geçmişi de silinir. Bağımlı operasyonel kayıt
            (lot, hareket, sipariş, iş emri, reçete…) varsa silme engellenir.
          </>
        }
        busy={delMut.isPending}
        errorMessage={delMut.error?.message ?? null}
        onConfirmed={runHardDelete}
        onClose={() => { setDelTarget(null); delMut.reset(); }}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <button
            type="button"
            className="btn-md primary"
            onClick={openCreate}
          ><Icon name="plus" size={15} />{labels.ctaLabel}</button>
          <div className="m-search">
            <Icon name="search" size={15} />
            <input type="search" placeholder="Kod veya ad ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
          <div className="m-chips">
            {TABS.map((t) => {
              const cnt = tabCounts[t.kind];
              return (
                <button
                  key={t.kind}
                  type="button"
                  className={`m-chip${kind === t.kind ? " act" : ""}`}
                  onClick={() => { setQ(""); clearFilters(); navigate(`/items/${KIND_LABELS[t.kind].routeSeg}`); }}
                >
                  {t.label}{cnt !== undefined ? ` (${cnt})` : ""}
                </button>
              );
            })}
          </div>
        </div>

        {activeQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : activeQ.isError ? (
          <div className="m-empty" style={{ color: "var(--danger)" }}>{activeQ.error?.message}</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{q || filtersActive ? "Sonuç yok" : `Henüz ${labels.title.toLowerCase()} yok`}</div>
        ) : (
          <>
            {rows.map((r) => {
              const stats: Array<[string, string]> = [["Birim", r.baseUom?.symbol ?? r.baseUom?.code ?? "—"]];
              if (kind === "stock") stats.push(["Alış Fiyatı", r.currentPrice ? fmtPrice(r.currentPrice) : "—"]);
              stats.push(["Raf Ömrü", r.shelfLifeDays ? `${r.shelfLifeDays} gün` : "—"]);
              return (
                <MCard pad key={r.id}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.name}</span>
                      <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.code}</span>
                    </span>
                    {r.isActive ? <span className="badge b-success no-dot">Aktif</span> : <span className="badge b-neutral no-dot">Pasif</span>}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    {stats.map(([lb, v]) => (
                      <div key={lb} style={{ flex: 1 }}>
                        <div className="m-ovl">{lb}</div>
                        <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)", marginTop: 2 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {r.allergens.length > 0 ? (
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
                      {r.allergens.map((a) => <span key={a} className="badge b-warning no-dot">{a}</span>)}
                    </div>
                  ) : null}
                  <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                    {kind === "stock" ? (
                      <button type="button" className="m-iconbtn m-li-act" aria-label="Birim fiyat geçmişi" onClick={() => setPriceCard(r)}><Icon name="chart" size={16} /></button>
                    ) : null}
                    <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle / detay" onClick={() => openEdit(r)}><Icon name="edit" size={16} /></button>
                    {isSuperAdmin ? (
                      <button type="button" className="m-iconbtn m-li-act danger" aria-label="Kalıcı sil" onClick={() => { delMut.reset(); setDelTarget(r); }}><Icon name="trash" size={16} /></button>
                    ) : null}
                  </div>
                </MCard>
              );
            })}
            {totalPages > 1 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
                <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
                <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
                <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
              </div>
            ) : null}
          </>
        )}
        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">{labels.title}</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">{labels.title}</h1>
          <p className="sub">{labels.sub}</p>
        </div>
        <button type="button" className="btn-md primary" onClick={openCreate}>
          <Icon name="plus" size={14} />{labels.ctaLabel}
        </button>
      </div>

      <div className="dt2-wrap">
        <div className="dt2-tabs">
          {TABS.map((t) => {
            const cnt = tabCounts[t.kind];
            return (
              <button
                key={t.kind}
                type="button"
                className={`dt2-tab ${kind === t.kind ? "active" : ""}`}
                onClick={() => {
                  setQ("");
                  clearFilters();
                  navigate(`/items/${KIND_LABELS[t.kind].routeSeg}`);
                }}
              >
                {t.label} {cnt !== undefined ? <span className="ct">{cnt}</span> : null}
              </button>
            );
          })}
        </div>

        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Kod veya ad ara…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              aria-label="Ara"
            />
          </div>

          <div className="dt2-filter">
            <SearchSelect
              value={typeId || null}
              options={typeOptions}
              onChange={(v) => { setTypeId(v ?? ""); setPage(1); }}
              placeholder="Tür"
              searchPlaceholder="Tür ara…"
              emptyText="Tür yok"
            />
          </div>
          <div className="dt2-filter sm">
            <SearchSelect
              value={uomId || null}
              options={uomOptions}
              onChange={(v) => { setUomId(v ?? ""); setPage(1); }}
              placeholder="Birim"
              searchPlaceholder="Birim ara…"
              emptyText="Birim yok"
            />
          </div>
          <div className="dt2-filter sm">
            <SearchSelect
              value={status === "all" ? null : status}
              options={statusOptions}
              onChange={(v) => { setStatus((v as "active" | "passive") ?? "all"); setPage(1); }}
              placeholder="Durum"
              showFooter={false}
              emptyText="—"
            />
          </div>
          <div className="dt2-filter">
            <SearchSelect
              value={allergen || null}
              options={allergenOptions}
              onChange={(v) => { setAllergen(v ?? ""); setPage(1); }}
              placeholder="Alerjen"
              searchPlaceholder="Alerjen ara…"
              emptyText="Alerjen yok"
            />
          </div>
          {filtersActive ? (
            <button type="button" className="btn" onClick={clearFilters} title="Filtreleri temizle">
              <Icon name="x" size={13} />Temizle
            </button>
          ) : null}

          <span className="grow" />
          <button type="button" className="btn" onClick={() => activeQ.refetch()} disabled={activeQ.isFetching}>
            {activeQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>

        <div className="dt2-scroll">
          {activeQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : activeQ.isError ? (
            <div className="empty-state">
              <p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p>
              <p className="sub">{activeQ.error?.message}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">{q || filtersActive ? "Sonuç yok" : "Henüz kayıt yok"}</p>
              <p className="sub">
                {q
                  ? `"${q}" için sonuç yok.`
                  : filtersActive
                    ? "Seçili filtrelere uyan kayıt yok."
                    : `İlk ${labels.title.toLowerCase()} kaydını ekleyin.`}
              </p>
              {q || filtersActive ? (
                <button type="button" className="btn-md es-action" onClick={() => { setQ(""); clearFilters(); }}>
                  Filtreleri temizle
                </button>
              ) : (
                <button type="button" className="btn-md primary es-action" onClick={openCreate}>
                  <Icon name="plus" size={14} />{labels.ctaLabel}
                </button>
              )}
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Kod</th>
                  <th>Ad</th>
                  <th style={{ width: 100 }}>Birim</th>
                  {kind === "stock" ? <th className="num" style={{ width: 120 }}>Alış Fiyatı</th> : null}
                  <th className="num" style={{ width: 110 }}>Raf Ömrü</th>
                  <th>Alerjenler</th>
                  <th style={{ width: 90 }}>Durum</th>
                  <th style={{ width: 80 }}>Eylemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const shelf = r.shelfLifeDays;
                  return (
                    <tr key={r.id}>
                      <td><span className="cell-id">{r.code}</span></td>
                      <td>
                        {r.name}
                        {r.description ? <span className="sec">{r.description}</span> : null}
                      </td>
                      <td>{r.baseUom?.symbol ?? r.baseUom?.code ?? "—"}</td>
                      {kind === "stock" ? (
                        <td className="cell-num">
                          {r.currentPrice ? fmtPrice(r.currentPrice) : <span style={{ color: "var(--neutral-400)" }}>—</span>}
                        </td>
                      ) : null}
                      <td className="cell-num">
                        {shelf ? <>{shelf}<span className="unit">gün</span></> : <span style={{ color: "var(--neutral-400)" }}>—</span>}
                      </td>
                      <td>
                        {r.allergens.length === 0 ? (
                          <span style={{ color: "var(--neutral-400)", fontSize: 11 }}>—</span>
                        ) : (
                          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
                            {r.allergens.map((a) => (
                              <span key={a} className="badge-pill b-warn">{a}</span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td>
                        {r.isActive
                          ? <span className="badge-pill b-success">Aktif</span>
                          : <span className="badge-pill">Pasif</span>}
                      </td>
                      <td>
                        <div className="row-acts">
                          {kind === "stock" ? (
                            <button type="button" className="iact" aria-label="Birim fiyat geçmişi" title="Birim fiyat" onClick={() => setPriceCard(r)}>
                              <Icon name="chart" size={15} />
                            </button>
                          ) : null}
                          <button type="button" className="iact" aria-label="Düzenle / detay" title="Düzenle / detay" onClick={() => openEdit(r)}>
                            <Icon name="edit" size={15} />
                          </button>
                          {isSuperAdmin ? (
                            <button type="button" className="iact danger" aria-label="Kalıcı sil" title="Kalıcı sil (süper admin)" onClick={() => { delMut.reset(); setDelTarget(r); }}>
                              <Icon name="trash" size={15} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div>
            <b>{total}</b> kayıttan <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası
          </div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      {drawers}
    </div>
  );
}
